import { createSlice } from '@reduxjs/toolkit';
import { clearSessionData } from '@shared/lib/sessionStorage';
import { clearFilterHistory } from '@shared/lib/filterHistory';
import { clearStoredTableFilters } from '@shared/lib/tableFilters';
import {
  login,
  logout,
  endSession,
  getUser,
  getMetadata,
  updateUser,
  checkIsLogin,
  setIsLoginToStorage,
  saveUserLanguage,
} from '../api';
import isEqual from 'lodash/isEqual';
import { REQUEST_STATUS } from '../constants';
import { checkPermission } from '@shared/lib/checkPermissions';
import permissions from '@shared/config/permissions';
import { clearIdleStore, writeLastInteractionAt } from '@shared/lib/idleSession';

const userInitialState = {
  isLogin: null,
  isLoginStatus: REQUEST_STATUS.idle,
  isLoginErrors: {},
  initPath: null,

  isMaintenanceMode: false,

  hipaaCheckParams: null,
  // The account whose HIPAA agreement this tab has confirmed. In memory on
  // purpose: a reload or a new tab starts at null and checks again. The API
  // does not enforce the agreement, so this is the only gate.
  hipaaPassedAccountUuid: null,

  metadata: null,
  metadataStatus: REQUEST_STATUS.idle,

  user: null,
  status: REQUEST_STATUS.idle,
  error: null,

  blockedAccounts: [],

  isDistributionSponsor: false,
};

// Everything a sign-out drops, whichever path ends the session: the
// user's own logout, the idle logout (`endSession`) or a login-page hash
// after a 410 (`sessionCleared`).
const clearSignedInState = state => {
  state.isLogin = false;
  state.isLogoutStatus = REQUEST_STATUS.succeeded;
  // A sign-in only reaches `succeeded` once the HIPAA check passes;
  // cancelling that check signs out with the status still `loading`,
  // which would leave the login button spinning and disabled. Start the
  // next attempt clean.
  state.isLoginStatus = REQUEST_STATUS.idle;
  state.isLoginErrors = {};
  state.user = null;
  state.metadata = null;
  state.isDistributionSponsor = false;
  state.hipaaCheckParams = null;
  state.hipaaPassedAccountUuid = null;
  state.blockedAccounts = [];
  clearSessionData();
  clearIdleStore();
  // The table filters and their history live one session (they can hold
  // patient names).
  clearFilterHistory();
  clearStoredTableFilters();
  // Last: the other tabs of the session watch this key and leave too.
  setIsLoginToStorage(false);
};

export const userSlice = createSlice({
  name: 'user',
  initialState: userInitialState,
  reducers: {
    updateIsMaintenanceMode: (state, action) => {
      state.isMaintenanceMode = action.payload;
    },
    getIsLogin: state => {
      state.isLogin = checkIsLogin() === 'true';
    },
    setIsLogin: (state, action) => {
      setIsLoginToStorage(action.payload);
      state.isLogin = action.payload;
    },
    // Local-only sign-out: the API side is already done (or gone).
    sessionCleared: clearSignedInState,
    setIsLoginStatus: (state, action) => {
      state.isLoginStatus = action.payload;
    },
    setBlockedAccounts: (state, action) => {
      state.blockedAccounts = action.payload;
    },
    setUser: (state, action) => {
      if (!isEqual(state.user, action.payload)) {
        state.user = action.payload;
      }
    },
    setHipaaPassedAccountUuid: (state, action) => {
      state.hipaaPassedAccountUuid = action.payload;
    },
    setHipaaCheckParams: (state, action) => {
      if (!isEqual(state.hipaaCheckParams, action.payload)) {
        state.hipaaCheckParams = action.payload;
      }
    },
    setMetadata: (state, action) => {
      const isDistributionSponsor = checkPermission(
        action.payload.currentAccountPermissions,
        permissions.ACCOUNTS_GET
      );
      state.metadata = action.payload;
      state.isDistributionSponsor = isDistributionSponsor;
    },
    setInitPath: (state, action) => {
      state.initPath = action.payload;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(login.pending, state => {
        state.isLoginStatus = REQUEST_STATUS.loading;
        state.isLoginErrors = {};
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoginStatus = REQUEST_STATUS.failed;
      })
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload.ok) {
          if (!action.payload.data.data.authMetadata) {
            state.isLoginStatus = REQUEST_STATUS.idle;
            return;
          }
          clearSessionData();
          // Also covers a browser closed without logging out: whatever
          // filters and filter history that session left start over here.
          clearFilterHistory();
          clearStoredTableFilters();
          setIsLoginToStorage(true);
          // The idle timer counts from here (features/session-idle).
          writeLastInteractionAt(Date.now());
          state.isLogin = true;
          state.hipaaCheckParams = {
            newUser: action.payload.data.data.user,
            newMetadata: action.payload.data.data.authMetadata,
          };
        } else {
          state.isLoginStatus = REQUEST_STATUS.failed;
          if (action.payload.status === 422) {
            state.isLoginErrors = action.payload.data.validationErrors;
          }
        }
      })
      .addCase(logout.pending, state => {
        state.isLogoutStatus = REQUEST_STATUS.loading;
        state.isLoginErrors = {};
      })
      // The user asked to leave: drop the signed-in state whatever the API
      // answered, like the idle logout does. Keeping the user, the account
      // metadata and the isLogin flag after a failed DELETE would let Back
      // or a reload walk into the app again on a shared workstation, while
      // the screen already shows the login page.
      .addCase(logout.rejected, state => {
        clearSignedInState(state);
        state.isLogoutStatus = REQUEST_STATUS.failed;
      })
      .addCase(logout.fulfilled, (state, action) => {
        clearSignedInState(state);
        if (!action.payload?.ok) {
          state.isLogoutStatus = REQUEST_STATUS.failed;
        }
      })
      .addCase(endSession.fulfilled, clearSignedInState)
      .addCase(getUser.pending, state => {
        if (state.status === REQUEST_STATUS.idle) {
          state.status = 'idle_loading';
        } else {
          state.status = REQUEST_STATUS.loading;
        }
      })
      .addCase(getUser.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
      })
      // PATCH /user answers with the updated user resource; keep the
      // store's copy current so `preferredLanguage` reflects the save.
      .addCase(saveUserLanguage.fulfilled, (state, action) => {
        if (action.payload?.ok && action.payload.data?.data) {
          state.user = action.payload.data.data;
        }
      })
      .addCase(getUser.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.user = action.payload.data.data;
          state.status = REQUEST_STATUS.succeeded;
        } else {
          state.status = REQUEST_STATUS.failed;
          if (action.error) {
            state.error = action.error.message;
          }
        }
      })
      .addCase(updateUser.pending, state => {
        state.status = REQUEST_STATUS.loading;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.status = REQUEST_STATUS.failed;
        state.error = action?.error?.message;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.status = REQUEST_STATUS.succeeded;
        } else {
          state.status = REQUEST_STATUS.failed;
          state.error = action.error.message;
        }
      })
      .addCase(getMetadata.pending, state => {
        state.metadataStatus = REQUEST_STATUS.loading;
      })
      .addCase(getMetadata.rejected, (state, action) => {
        state.metadataStatus = REQUEST_STATUS.failed;
        state.metadata = null;
        state.isDistributionSponsor = false;
      })
      .addCase(getMetadata.fulfilled, (state, action) => {
        if (action.payload.ok) {
          state.metadataStatus = REQUEST_STATUS.succeeded;
          state.metadata = action.payload.data.data;
          const isDistributionSponsor = action?.payload?.data?.data?.currentAccountPermissions
            ? checkPermission(
                action?.payload?.data?.data?.currentAccountPermissions,
                permissions.ACCOUNTS_GET
              )
            : false;
          state.isDistributionSponsor = isDistributionSponsor;
        } else {
          state.metadataStatus = REQUEST_STATUS.failed;
          state.metadata = null;
          state.isDistributionSponsor = false;
        }
      });
  },
});

export const {
  updateIsMaintenanceMode,
  setUser,
  setHipaaCheckParams,
  setHipaaPassedAccountUuid,
  setMetadata,
  getIsLogin,
  setIsLogin,
  sessionCleared,
  setIsLoginStatus,
  setBlockedAccounts,
  setInitPath,
} = userSlice.actions;

export default userSlice.reducer;
